import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { PeriodType } from '../../types/prompt/PeriodType';
import { Nullable } from 'primereact/ts-helpers';
import { ChatType } from '../../types/monitor/ChatType';
import { FirmData } from '../../models/monitor/FirmData';
import { useParams, useSearchParams } from 'react-router-dom';
import useMonitorPeriodAndChatType from '../../hooks/filters/UseMonitorPeriodAndChatType';
import { useAccount } from '../account/AccountContexts';
import { useGetProject } from '../../api/hooks/project/useGetProject';
import { MonitorType } from '../../types/monitor/MonitorType';
import { Addons } from '../../types/account/Addons';
import { DateRange } from '../../models/monitor/DateRange';
import useDateRange from '../../hooks/filters/UseDateRange';
import { StandardPeriodType } from '../../types/filters/StandardPeriodType';
import { chatTypeOptions } from '../../helpers/MonitorTypeHelpers';

interface FiltersContextType {
  monitorPeriod: PeriodType | null;
  setMonitorPeriod: Dispatch<SetStateAction<PeriodType | null>>;
  selectedFirm: FirmData | null;
  setSelectedFirm: Dispatch<SetStateAction<FirmData | null>>;
  range: Nullable<(Date | null)[]>;
  setRange: Dispatch<SetStateAction<Nullable<(Date | null)[]>>>;
  selectedChatType: ChatType | null;
  setSelectedChatType: Dispatch<SetStateAction<ChatType | null>>;
  selectedChatTypes: ChatType[];
  setSelectedChatTypes: Dispatch<SetStateAction<ChatType[]>>;
  domain: string | null;
  setDomain: Dispatch<SetStateAction<string | null>>;
  dateRange: DateRange | null;
  selectedPeriod: StandardPeriodType | null;
  setSelectedPeriod: Dispatch<SetStateAction<StandardPeriodType | null>>;
}

export const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

const parseModelsParam = (
  modelsParam: string | null,
  chat: string | undefined,
  availableTypes: ChatType[]
): ChatType[] => {
  if (modelsParam) {
    const models = modelsParam.split(',').map((m) => m.toUpperCase() as ChatType);
    const valid = models.filter((m) => availableTypes.includes(m));
    return valid.length > 0 ? valid : availableTypes;
  }

  if (chat && chat.toUpperCase() !== 'ALL') {
    const chatUpper = chat.toUpperCase() as ChatType;
    if (availableTypes.includes(chatUpper)) {
      return [chatUpper];
    }
  }

  return availableTypes;
};

const deriveEffectiveChatType = (
  types: ChatType[],
  allAvailable: ChatType[],
  hasNewStats: boolean
): ChatType => {
  // NEW_STATS: when the selection covers all account models (the default, incl. a single-model
  // account), use the ALL view so archival models still show. An explicit subset filter keeps its model.
  if (hasNewStats && types.length >= allAvailable.length) return 'ALL';
  if (types.length === 1) return types[0];
  if (types.length >= allAvailable.length) return 'ALL';
  return 'ALL';
};

export const FiltersContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [monitorPeriod, setMonitorPeriod] = useState<PeriodType | null>(null);
  const [selectedFirm, setSelectedFirm] = useState<FirmData | null>(null);
  // The range starts as null and is set only by useDateRange (after /date-range responds, with a
  // fallback on error). Seeding a provisional range here made the stats queries fire with wrong
  // dates and refire moments later.
  const [range, setRange] = useState<Nullable<(Date | null)[]>>(null);
  const [selectedChatType, setSelectedChatType] = useState<ChatType | null>(null);
  const [selectedChatTypes, setSelectedChatTypes] = useState<ChatType[]>([]);
  const [domain, setDomain] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<StandardPeriodType | null>(null);

  const { type, period, chat, projectId } = useParams();
  const [searchParams] = useSearchParams();
  // `Number('dsadas')` to NaN, a porównanie niżej (`boundProjectId !== projectIdNumber`) jest dla
  // NaN ZAWSZE prawdziwe — ustawienie stanu w renderze leciało wtedy w kółko i React zabijał stronę
  // („Too many re-renders"). Adres z niepoprawnym id traktujemy jak brak projektu; o odesłaniu
  // użytkownika decyduje strona, nie ten kontekst.
  const parsedProjectId = Number(projectId);
  const projectIdNumber = projectId && Number.isFinite(parsedProjectId) ? parsedProjectId : 0;

  // The provider survives a project switch (same route element, only the :projectId param changes),
  // so the previous project's range must be dropped before children render with the new projectId.
  // An effect would be one render too late: child queries fire in child effects, which run BEFORE
  // this component's effects, so one frame with (new projectId, old range) would still hit the
  // network. Resetting during render (React's "adjust state when a prop changes" pattern)
  // re-renders immediately, before children ever see that pair.
  const [boundProjectId, setBoundProjectId] = useState(projectIdNumber);
  if (boundProjectId !== projectIdNumber) {
    setBoundProjectId(projectIdNumber);
    setRange(null);
    setDateRange(null);
  }

  const { accountResponse } = useAccount();
  // When a superadmin views a client's project, feature-gating (available models + NEW_STATS) must come
  // from the OWNER account, not the superadmin's — otherwise the model filter shows the wrong models.
  // project.ownerFeatures/ownerAddons are set only in that case; own/non-admin views fall back.
  const { project } = useGetProject(projectIdNumber || null);
  const effectiveFeatures = useMemo(
    () => (project?.ownerFeatures ?? accountResponse?.features ?? []) as MonitorType[],
    [project?.ownerFeatures, accountResponse?.features]
  );
  const effectiveAddons = useMemo(
    () => (project?.ownerAddons ?? accountResponse?.addons ?? []) as Addons[],
    [project?.ownerAddons, accountResponse?.addons]
  );
  const effectiveAccount = useMemo(
    () =>
      accountResponse
        ? { ...accountResponse, features: effectiveFeatures, addons: effectiveAddons }
        : null,
    [accountResponse, effectiveFeatures, effectiveAddons]
  );

  const { monitorPeriod: monitorPeriodContext, selectedChatType: selectedChatTypeContext } =
    useMonitorPeriodAndChatType(period, chat, effectiveAccount);

  const availableChatTypes = useMemo(() => {
    if (!accountResponse) return [];
    return chatTypeOptions(effectiveFeatures)
      .filter((opt) => opt.type !== 'ALL' && !opt.separator)
      .map((opt) => opt.type);
  }, [accountResponse, effectiveFeatures]);

  useEffect(() => {
    setMonitorPeriod(monitorPeriodContext);

    if (availableChatTypes.length === 0) {
      const modelsParam = searchParams.get('models');
      if (!modelsParam) {
        setSelectedChatType(selectedChatTypeContext);
      }
      return;
    }

    const modelsParam = searchParams.get('models');
    const parsedTypes = parseModelsParam(modelsParam, chat, availableChatTypes);
    setSelectedChatTypes(parsedTypes);

    const hasNewStats = effectiveAddons.includes('NEW_STATS');
    const effective = deriveEffectiveChatType(parsedTypes, availableChatTypes, hasNewStats);
    setSelectedChatType(effective);
  }, [monitorPeriodContext, selectedChatTypeContext, chat, searchParams, availableChatTypes, accountResponse, effectiveAddons]);

  useDateRange(projectIdNumber, setDateRange, setRange, setSelectedPeriod);

  return (
    <FiltersContext.Provider
      value={{
        monitorPeriod,
        selectedFirm,
        range,
        selectedChatType,
        selectedChatTypes,
        domain,
        dateRange,
        selectedPeriod,
        setMonitorPeriod,
        setSelectedFirm,
        setRange,
        setSelectedChatType,
        setSelectedChatTypes,
        setDomain,
        setSelectedPeriod,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw Error('useFilters must be used with an FiltersContextProvider');
  }

  return context;
};
